import {
    ArrowDownOutlined, ArrowUpOutlined, CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Checkbox, Empty, Flex, Input, Modal, Space, Spin, Tag, Typography,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';

import {
    scriptsMutations, scriptsQueries,
} from '@/api';
import { ConfirmModal } from '@/components';
import { Script } from '@/types';

type CrossSellsModalProps = {
    open: boolean;
    projectId: string;
    scriptId?: string;
    currentCrossSells?: Script[];
    onClose: () => void;
    onSuccess?: () => void;
};

export const CrossSellsModal = ({
    open, projectId, scriptId, currentCrossSells = [], onClose, onSuccess,
}: CrossSellsModalProps) => {
    const [selectedScripts, setSelectedScripts] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedScriptsOrder, setSelectedScriptsOrder] = useState<string[]>([]);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [applyToAll, setApplyToAll] = useState(false);

    const { data: scriptsData, isLoading: isLoadingScripts } = useQuery(
        scriptsQueries.getAllScripts(projectId, {
            type: 'cross-sale',
            per_page: -1,
            current_page: 1,
        }),
    );

    const attachMutationOptions = useMemo(
        () => scriptId ? scriptsMutations.attachCrossSell(projectId, scriptId) : null,
        [projectId, scriptId],
    );
    const { mutate: attachCrossSells, isPending: isAttaching } = useMutation(
        attachMutationOptions || {
            mutationKey: ['scripts', 'scripts:attach-cross-sell', 'noop'],
            mutationFn: async () => ({} as Script),
        },
    );
    const { mutate: bulkAttach, isPending: isBulkAttaching } = useMutation(
        scriptsMutations.bulkAttachCrossSell(projectId),
    );
    const { mutate: bulkDetach, isPending: isBulkDetaching } = useMutation(
        scriptsMutations.bulkDetachCrossSell(projectId),
    );

    useEffect(() => {
        if (open && currentCrossSells) {
            const ids = currentCrossSells.map(s => s.id);
            setSelectedScripts(ids);
            setSelectedScriptsOrder(ids);
            setSearchText('');
            setConfirmModalOpen(false);
            setApplyToAll(false);
        }
    }, [open, currentCrossSells]);

    const handleScriptChange = useCallback((scriptId: string, checked: boolean) => {
        if (checked) {
            setSelectedScripts((prev) => [...prev, scriptId]);
            setSelectedScriptsOrder((prev) => [...prev, scriptId]);
        } else {
            setSelectedScripts((prev) => prev.filter((id) => id !== scriptId));
            setSelectedScriptsOrder((prev) => prev.filter((id) => id !== scriptId));
        }
    }, []);

    const handleSelectAll = useCallback(() => {
        if (scriptsData?.items) {
            const allIds = scriptsData.items
                .filter(s => s.id !== scriptId)
                .map(s => s.id);
            setSelectedScripts(allIds);
            setSelectedScriptsOrder(allIds);
        }
    }, [scriptsData?.items, scriptId]);

    const handleDeselectAll = useCallback(() => {
        setSelectedScripts([]);
        setSelectedScriptsOrder([]);
    }, []);

    const handleMoveUp = useCallback((index: number) => {
        if (index > 0) {
            setSelectedScriptsOrder((prev) => {
                const newOrder = [...prev];
                [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                return newOrder;
            });
        }
    }, []);

    const handleMoveDown = useCallback((index: number) => {
        setSelectedScriptsOrder((prev) => {
            if (index < prev.length - 1) {
                const newOrder = [...prev];
                [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                return newOrder;
            }
            return prev;
        });
    }, []);

    const handleSave = useCallback(() => {
        setConfirmModalOpen(true);
    }, []);

    const handleConfirmSave = useCallback(() => {
        if (!scriptId) return;

        setConfirmModalOpen(false);

        const sortOrders: Record<string, number> = {};
        selectedScriptsOrder.forEach((id, index) => {
            sortOrders[id] = index;
        });

        const currentIds = currentCrossSells.map(s => s.id);
        const addedIds = selectedScripts.filter(id => !currentIds.includes(id));
        const removedIds = currentIds.filter(id => !selectedScripts.includes(id));

        attachCrossSells(
            { cross_sell_ids: selectedScripts, sort_orders: sortOrders },
            {
                onSuccess: () => {
                    if (applyToAll) {
                        if (addedIds.length > 0) {
                            bulkAttach({ cross_sell_ids: addedIds });
                        }
                        if (removedIds.length > 0) {
                            bulkDetach({ cross_sell_ids: removedIds });
                        }
                    }
                    onClose();
                    onSuccess?.();
                },
                onError: () => {},
            },
        );
    }, [scriptId, attachCrossSells, bulkAttach, bulkDetach, applyToAll, selectedScripts, selectedScriptsOrder, currentCrossSells, onClose, onSuccess]);

    const handleCancelConfirm = useCallback(() => {
        setConfirmModalOpen(false);
    }, []);

    const filteredScripts = useMemo(() => {
        if (!scriptsData?.items) return [];

        return scriptsData.items.filter(
            (script) =>
                script.id !== scriptId &&
                (script.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                script.product_sku?.toLowerCase().includes(searchText.toLowerCase()) ||
                script.product_id?.toLowerCase().includes(searchText.toLowerCase())),
        );
    }, [scriptsData?.items, searchText, scriptId]);

    const isLoading = isLoadingScripts;
    const isPending = isAttaching || isBulkAttaching || isBulkDetaching;

    return (
        <Modal
            open={open}
            title="Вибір крос-сейлів"
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={isPending}>
                    Скасувати
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSave}
                    loading={isPending}
                    disabled={!scriptId || isPending}
                >
                    Зберегти
                </Button>,
            ]}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Flex justify="space-between" align="center">
                    <Input
                        placeholder="Пошук по скриптах..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 400 }}
                        allowClear
                        disabled={isPending}
                    />
                    <Space>
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                            Вибрано: {selectedScripts.length}
                        </Tag>
                    </Space>
                </Flex>

                <Flex justify="space-between" align="center">
                    <Space>
                        <Button
                            size="small"
                            onClick={handleSelectAll}
                            disabled={isPending || filteredScripts.length === 0}
                        >
                            Вибрати всі
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDeselectAll}
                            disabled={isPending || selectedScripts.length === 0}
                        >
                            Зняти всі
                        </Button>
                    </Space>
                    <Checkbox
                        checked={applyToAll}
                        onChange={(e) => setApplyToAll(e.target.checked)}
                        disabled={isPending}
                    >
                        Застосувати до всіх скриптів проекту
                    </Checkbox>
                </Flex>

                {selectedScriptsOrder.length > 0 && (
                    <div>
                        <Typography.Text strong>Вибрані крос-сейли (порядок):</Typography.Text>
                        <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                            {selectedScriptsOrder.map((scriptId, index) => {
                                const script = scriptsData?.items?.find(s => s.id === scriptId);
                                if (!script) return null;

                                return (
                                    <Card
                                        key={scriptId}
                                        size="small"
                                        type="inner"
                                    >
                                        <Flex justify="space-between" align="center">
                                            <Space>
                                                <Typography.Text>{index + 1}.</Typography.Text>
                                                <Typography.Text>{script.title}</Typography.Text>
                                                <Tag color="orange">{script.product_sku}</Tag>
                                                {script.product_price && (
                                                    <Tag color="green">
                                                        {script.product_price}
                                                    </Tag>
                                                )}
                                            </Space>
                                            <Space>
                                                <Button
                                                    size="small"
                                                    icon={<ArrowUpOutlined />}
                                                    onClick={() => handleMoveUp(index)}
                                                    disabled={isPending || index === 0}
                                                />
                                                <Button
                                                    size="small"
                                                    icon={<ArrowDownOutlined />}
                                                    onClick={() => handleMoveDown(index)}
                                                    disabled={isPending || index === selectedScriptsOrder.length - 1}
                                                />
                                                <Button
                                                    size="small"
                                                    danger
                                                    onClick={() => handleScriptChange(scriptId, false)}
                                                    disabled={isPending}
                                                >
                                                    Видалити
                                                </Button>
                                            </Space>
                                        </Flex>
                                    </Card>
                                );
                            })}
                        </Space>
                    </div>
                )}

                {isLoading ? (
                    <Flex justify="center" align="center" style={{ padding: 40 }}>
                        <Spin size="large" />
                    </Flex>
                ) : (
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Typography.Text strong>Доступні скрипти:</Typography.Text>
                        <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                            {filteredScripts
                                .filter(script => !selectedScripts.includes(script.id))
                                .map((script) => (
                                    <Card
                                        key={script.id}
                                        size="small"
                                    >
                                        <Checkbox
                                            checked={false}
                                            onChange={(e) =>
                                                handleScriptChange(script.id, e.target.checked)
                                            }
                                            disabled={isPending}
                                        >
                                            <Space>
                                                {script.title}
                                                <Tag color="orange">{script.product_sku}</Tag>
                                                {script.product_price && (
                                                    <Tag color="green">
                                                        {script.product_price}
                                                    </Tag>
                                                )}
                                            </Space>
                                        </Checkbox>
                                    </Card>
                                ))}
                        </Space>

                        {filteredScripts.filter(script => !selectedScripts.includes(script.id)).length === 0 && (
                            <Empty description="Всі доступні скрипти вже вибрані" />
                        )}
                    </div>
                )}
            </Space>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="script"
                entityName="крос-сейли"
                onConfirm={handleConfirmSave}
                onCancel={handleCancelConfirm}
                loading={isPending}
            />
        </Modal>
    );
};
